import React, { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "../App";
import { NavLink, useLocation } from "react-router-dom";
import { FaLinkedin, FaCog, FaBars, FaTimes } from "react-icons/fa";
import { BsFillSunFill, BsFillMoonFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { useLanguage } from "./../contexts/LanguageContext";

const LINKEDIN_URL =
  "https://www.linkedin.com/in/ga%C3%ABlle-barbieri-940815375/";

const Navbar = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { language, changeLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const menus = useMemo(
    () => [
      { path: "/", label: t("navbar.home") },
      { path: "/about", label: t("navbar.about") },
      { path: "/news", label: t("navbar.news") },
      { path: "/prestations", label: t("navbar.prestations") },
      { path: "/prise_rdv", label: t("navbar.priseRDV"), cta: true },
      { path: "/contact", label: t("navbar.contact") },
      { path: "/appli/dashboard", label: <FaCog size={18} />, iconOnly: true },
    ],
    [t]
  );

  // ✅ FIX 1 : on évite backdrop-blur sur le <nav> quand le menu mobile est ouvert
  const navShell = `fixed top-0 w-full z-50 transition-all duration-300 ${
    isMenuOpen
      ? "bg-bgbackground bg-opacity-95 dark:bg-neutral-950 dark:bg-opacity-95 border-b border-black/10 dark:border-white/10"
      : isScrolled
      ? "bg-bgbackground bg-opacity-90 dark:bg-neutral-950 dark:bg-opacity-90 backdrop-blur-xl " +
        "shadow-[0_10px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_30px_rgba(0,0,0,0.45)] " +
        "border-b border-black/10 dark:border-white/10"
      : "bg-transparent"
  }`;

  const desktopLinkBase =
    "group relative inline-flex items-center px-2.5 pt-2 pb-4 font-medium transition-colors duration-200";

  const desktopLinkTone =
    "text-gray-800 dark:text-gray-100 hover:text-primary dark:hover:text-primary";

  const desktopCTA =
    "inline-flex items-center px-4 py-1 rounded-2xl bg-primary text-white hover:bg-primary-dark " +
    "shadow-md hover:shadow-lg transition-all duration-200";

  const iconBtn =
    "w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-200 " +
    "bg-primary text-white hover:bg-primary-dark shadow-sm hover:shadow-md";

  const underline = (active) =>
    `absolute left-0 right-0 bottom-0 h-[2px] bg-primary transform origin-left transition-transform duration-300 ease-out ${
      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
    }`;

  return (
    <>
      <nav className={navShell}>
        <div className="mx-auto flex justify-between items-center py-2 px-6 max-w-[2000px]">
          {/* Brand */}
          <NavLink
            to="/"
            className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Accueil"
          >
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-japanTitle capitalize font-bold text-primary dark:text-white transition-transform duration-200 group-hover:translate-x-[1px]">
                Gaëlle Prévention Shiatsu
              </span>
            </div>
          </NavLink>

          {/* Burger */}
          <button
            className="lg1096:hidden text-primary text-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl p-2"
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* Desktop menu */}
          <ul className="hidden lg1096:flex lg1096:space-x-2 xl:space-x-3 font-satoshi items-center">
            {menus.map((menu) => {
              const isCta = !!menu.cta;
              const isIcon = !!menu.iconOnly;

              return (
                <li key={menu.path} className="relative">
                  <NavLink
                    to={menu.path}
                    className={({ isActive }) => {
                      if (isIcon) {
                        return `inline-flex items-center justify-center ${iconBtn} ${
                          isActive ? "ring-2 ring-white/60" : ""
                        }`;
                      }
                      if (isCta) return desktopCTA;

                      return `${desktopLinkBase} ${desktopLinkTone} ${
                        isActive ? "text-primary dark:text-primary" : ""
                      }`;
                    }}
                  >
                    {({ isActive }) => (
                      <span className="relative">
                        {menu.label}
                        {!menu.iconOnly && !menu.cta && (
                          <span className={underline(isActive)} />
                        )}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {/* Right side controls */}
          <div className="hidden lg1096:flex items-center space-x-4">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center w-9 h-9 bg-primary text-white rounded-2xl hover:bg-primary-dark transition-all duration-200 shadow-sm hover:shadow-md"
              aria-label="LinkedIn"
              title="LinkedIn"
            >
              <FaLinkedin size={16} />
            </a>

            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-transparent text-gray-800 dark:text-gray-100 hover:text-primary rounded-xl px-3 py-2
                         ring-1 ring-black/10 dark:ring-white/15
                         focus:outline-none focus:ring-2 focus:ring-primary transition"
              aria-label="Langue"
            >
              <option value="FR">Français</option>
              <option value="EN">English</option>
            </select>

            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-2xl flex items-center justify-center
                         text-primary bg-black/5 dark:bg-white/10
                         hover:bg-black/10 dark:hover:bg-white/15
                         transition-all duration-200
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={
                theme === "light"
                  ? "Activer le mode sombre"
                  : "Activer le mode clair"
              }
              title={theme === "light" ? "Mode sombre" : "Mode clair"}
            >
              {theme === "light" ? (
                <BsFillMoonFill size={20} />
              ) : (
                <BsFillSunFill size={20} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* ✅ FIX 2 : Mobile drawer déplacé HORS du <nav> (sinon bug backdrop-filter/fixed) */}
      <div
        className={`lg1096:hidden fixed inset-0 z-[70] ${
          isMenuOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        aria-hidden={!isMenuOpen}
      >
        <div
          className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
            isMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        <aside
          className={`absolute left-0 top-0 h-full w-[min(26rem,86vw)]
                      bg-bgbackground bg-opacity-95 dark:bg-neutral-950 dark:bg-opacity-95 backdrop-blur-xl
                      shadow-2xl border-r border-black/10 dark:border-white/10
                      transform transition-transform duration-300
                      ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="/images/fond_sakura.png"
              alt="Sakura"
              className="w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/25 to-transparent dark:from-black/70 dark:to-black/40" />
          </div>

          <div className="relative z-10 p-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                Gaëlle Prévention Shiatsu
              </div>

              <button
                className="w-10 h-10 rounded-2xl flex items-center justify-center
                           bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15
                           transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Fermer"
              >
                <FaTimes className="text-primary" />
              </button>
            </div>

            <div className="mt-4 h-[2px] bg-primary w-full rounded-full" />

            <div className="mt-6 flex flex-col gap-2">
              {menus.map((menu) => (
                <NavLink
                  key={menu.path}
                  to={menu.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) => {
                    const base =
                      "flex items-center justify-between px-4 py-3 rounded-2xl text-lg font-satoshi transition-colors duration-200";

                    if (menu.cta) {
                      return base + " bg-primary text-white hover:bg-primary-dark shadow-md";
                    }

                    if (menu.iconOnly) {
                      return (
                        base +
                        " border border-black/10 dark:border-white/15 text-gray-900 dark:text-white hover:text-primary"
                      );
                    }

                    return (
                      base +
                      ` border-l-2 ${
                        isActive
                          ? "border-primary text-primary"
                          : "border-transparent text-gray-900 dark:text-gray-100 hover:text-primary"
                      }`
                    );
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <span className="flex items-center gap-3">
                        {menu.iconOnly ? (
                          <>
                            <FaCog />
                            <span>{t("navbar.admin") || "Admin"}</span>
                          </>
                        ) : (
                          menu.label
                        )}
                      </span>

                      <span
                        className={`h-2 w-2 rounded-full transition-opacity ${
                          isActive ? "opacity-100 bg-primary" : "opacity-0 bg-primary"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </div>

            <div className="mt-auto pt-6 border-t border-black/10 dark:border-white/10">
              <div className="flex items-center justify-between">
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-primary text-white rounded-2xl hover:bg-primary-dark transition-all duration-200 shadow-sm hover:shadow-md"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                >
                  <FaLinkedin size={16} />
                </a>

                <button
                  onClick={toggleTheme}
                  className="w-10 h-10 rounded-2xl flex items-center justify-center
                             text-primary bg-black/5 dark:bg-white/10
                             hover:bg-black/10 dark:hover:bg-white/15
                             transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={
                    theme === "light"
                      ? "Activer le mode sombre"
                      : "Activer le mode clair"
                  }
                >
                  {theme === "light" ? (
                    <BsFillMoonFill size={20} />
                  ) : (
                    <BsFillSunFill size={20} />
                  )}
                </button>
              </div>

              <div className="mt-4">
                <select
                  value={language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="w-full bg-white/70 dark:bg-black/30 backdrop-blur-sm
                             text-gray-900 dark:text-gray-100
                             rounded-2xl px-3 py-3
                             ring-1 ring-black/10 dark:ring-white/15
                             focus:outline-none focus:ring-2 focus:ring-primary transition"
                  aria-label="Langue"
                >
                  <option value="FR">Français</option>
                  <option value="EN">English</option>
                </select>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Navbar;
